import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { format } from 'date-fns';
import { useQueryClient } from '@zg-rentals/react-query5';

import { Alert, Flex } from '@zillow/constellation';
import { Button, Paragraph, TextButton, Toast, useToast } from '@zillow/constellation-10';

import { useSelector } from '../../../../selectors/useSelector';

import ListingAvailabilityModal from '../../../Modal/ListingAvailabilityModal';
import { useConfirmAvailability } from '../../../../hooks/queries/availability';
import PropertiesActions from '../../../../actions/PropertiesActions';

import type { IAPIErrorResponse } from '../../../../interfaces';

const ListingAvailability = () => {
  const dispatch = useDispatch();
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();

  const {
    currentProperty: { expirationDateTimestamp, listingAlias },
  } = useSelector((state) => state.properties);

  const [displayLearnMoreModal, setDisplayLearnMoreModal] = useState(false);

  const { mutate: confirmListingAvailability } = useConfirmAvailability(listingAlias, {
    onError: (err: IAPIErrorResponse) => {
      if (displayLearnMoreModal) {
        setDisplayLearnMoreModal(false);
      }
      enqueueToast(
        <Toast tone="critical">
          {err?.error?.message || 'An error occurred while confirming your listing availability'}
        </Toast>,
        {
          duration: 10000,
        },
      );
    },
    onSuccess: (response: string) => {
      dispatch(
        PropertiesActions.updateCurrentProperty({
          expirationDateTimestamp: response,
        }),
      );
      queryClient.invalidateQueries({ queryKey: ['getPropertyDetailsOverview', listingAlias] });

      if (displayLearnMoreModal) {
        setDisplayLearnMoreModal(false);
      }

      enqueueToast(
        <Toast tone="success">
          {`Thanks for confirming! We'll keep your listing active until ${format(
            new Date(Number(response)),
            'MM/dd/yy',
          )}`}
        </Toast>,
        {
          duration: 10000,
        },
      );
    },
  });

  const handleLearnMoreClick = () => {
    setDisplayLearnMoreModal(true);
  };

  return (
    <Alert
      appearance="warning"
      body={
        <>
          <Flex
            display="flex"
            flexDirection={{ default: 'column', xl: 'row' }}
            alignItems={{ default: 'flex-start', xl: 'center' }}
            justifyContent={{ default: 'flex-start', xl: 'space-between' }}
          >
            <Flex>
              <Paragraph textStyle="body-sm-bold">Is this listing still available?</Paragraph>
              <Paragraph textStyle="body-sm" css={{ marginBottom: { base: '200', xl: 0 } }}>
                {`Listing expires ${format(new Date(Number(expirationDateTimestamp)), 'MMM dd, yyyy')}`}
              </Paragraph>
            </Flex>

            <Flex
              display="flex"
              flexDirection={{ default: 'column', xl: 'row' }}
              alignItems={{ default: 'flex-start', xl: 'center' }}
            >
              <Flex display="flex" order={{ default: 1, xl: 2 }}>
                <Button
                  css={{ marginBottom: { lgDown: '200' }, marginRight: { lgDown: '400' } }}
                  tone="brand"
                  emphasis="filled"
                  size="sm"
                  onClick={() => confirmListingAvailability()}
                >
                  Confirm availability
                </Button>
              </Flex>
              <Flex display="flex" order={{ default: 2, xl: 1 }}>
                <TextButton
                  onClick={handleLearnMoreClick}
                  textStyle="body-sm-bold"
                  css={{ marginRight: { xl: '400' } }}
                >
                  Learn more
                </TextButton>
              </Flex>
            </Flex>
          </Flex>

          {displayLearnMoreModal && (
            <ListingAvailabilityModal
              onConfirmClick={confirmListingAvailability}
              onModalClose={() => setDisplayLearnMoreModal(false)}
            />
          )}
        </>
      }
    />
  );
};

export default ListingAvailability;
